import React, { FormEvent, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import logoGov from '../../../assets/logo_govbr.svg';
import logoSisdaf from '../../../assets/logo_sisaf_negativo.png';
import capaSisdaf from '../../../assets/sisdaf_cadastro.png';
import Button from '../../../components/Button';
import ContainerBase from '../../../components/ContainerBase';
import { CustomSwal } from '../../../components/CustomFeedback/CustomSwal';
import Input from '../../../components/InputNew';
import { useCpfValidation } from '../../../modules/hooks/useCpfValidation';
import { usePasswordValidation } from '../../../modules/hooks/usePasswordValidation';
import httpClient, { isHttpError } from '../../../services/httpClient';
import GlobalStyle from '../../../utils/globalStyles';
import { swal } from '../../../utils/swal';

const ContainerCadastro = styled.div`
  display: grid;
  grid-template-columns: 30% 70%;
  margin: 0 auto;
  align-self: center;
  width: 85%;
  height: 75%;
  border-radius: 15px 15px 15px 15px;
`;

const SecaoEsquerda = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5vh;
  background: url(${capaSisdaf}) no-repeat center;
  background-size: cover;
  min-height: 100%;
  border-radius: 20px 0 0 20px;
  align-items: center;
  justify-content: start;
  color: white;

  img {
    max-height: 20vh;
    margin-top: 25%;
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  span {
    font-weight: bold;
    font-size: 3.5vh;
  }

  p {
    font-weight: normal;
    font-size: 2.5vh;
    width: 80%;
    line-height: 1.2;
  }
`;

const SecaoDireita = styled.div`
  display: grid;
  background-color: #fff;
  border-radius: 0 20px 20px 0;
  grid-template-rows: 10% 90%;
`;

const IconLogoContainer = styled.div`
  display: grid;
  grid-template-columns: 45% 55%;
  width: 100%;
  align-items: center;

  a {
    display: flex;
    align-self: center;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.botaoLaranja};
    font-size: 12px;
    padding: 0.25rem;
    margin-left: 2rem;
  }
  img {
    height: 2.5vh;
    max-height: 50px;
    width: auto;
  }
`;

const FormContainer = styled(Form)`
  align-self: center;
  justify-content: center;
  width: 75%;
  margin: 0 auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  margin-top: 5rem;

  button {
    width: 25%;
  }
`;

const Cadastrar: React.FC = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formError, setFormError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [nomeCompleto, setNomeUsuarioCadastro] = useState('');

  const [formData, setFormData] = useState({
    cpf: '',
    nomeCompleto: '',
    emailPessoal: '',
    celular: '',
    senha: '',
    unidadeCadastro: null,
  });
  const [confirmaSenha, setConfirmaSenha] = useState<string>('');

  const setSenha = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, senha: e.target.value });
    setPasswordDigits(e.target.value.length);
  };
  const handleConfirmaSenha = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmaSenha(e.target.value);
  };

  const [validLength, passwordMatch] = usePasswordValidation({
    firstPassword: formData.senha,
    secondPassword: confirmaSenha,
  });

  const [cpfDigits, setCpfDigits] = useState<number>(0);
  const [passwordDigits, setPasswordDigits] = useState<number>(0);
  const [validCpf] = useCpfValidation({ cpf: formData.cpf });

  const validaSenhaTamanho = () => {
    if (passwordDigits > 0) {
      if (!validLength) {
        swal(
          'error',
          'Senha inválida',
          'A senha deve conter no mínimo 6 caracteres!',
        );
        setFormData({ ...formData, senha: '' });
      }
    }
  };

  const validaSenhaConfirma = () => {
    if (passwordDigits >= 6) {
      if (!passwordMatch) {
        swal('error', 'Senha inválida', 'As senhas digitadas não são iguais!');
        setConfirmaSenha('');
      }
    }
  };

  const validaCPF = () => {
    if (cpfDigits > 0) {
      if (!validCpf) {
        swal(
          'error',
          'CPF Inválido',
          'O CPF digitado não é válido. Digite novamente.',
        );
        setFormData({ ...formData, cpf: '' });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Tira dígitos do CPF
    if (name === 'cpf') {
      setCpfDigits(value.replace(/\D/g, '').length);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const missingFields: string[] = [];

    if (!formData.cpf) missingFields.push('CPF');
    if (!formData.nomeCompleto) {
      missingFields.push('Nome Completo');
    } else if (formData.nomeCompleto.length < 12) {
      missingFields.push('Nome Completo (mínimo 12 caracteres)');
    }
    if (!formData.emailPessoal) missingFields.push('Email Pessoal');
    if (!formData.celular) missingFields.push('Celular');
    if (!formData.senha) missingFields.push('Senha');

    if (missingFields.length > 0) {
      CustomSwal.show({
        title: 'Campos Obrigatórios',
        html: `Por favor, preencha os seguintes campos: ${missingFields.join(
          ', ',
        )}.`,
        type: 'error',
        confirmText: 'Fechar',
      });

      setSubmitting(false);
      return;
    }

    // Validate password match before making the API call
    if (!passwordMatch) {
      CustomSwal.show({
        title: 'Erro na Confirmação da Senha',
        html: 'As senhas digitadas não são iguais!',
        type: 'error',
        confirmText: 'Fechar',
      });
      setFormData({ ...formData, senha: '' }); // Clear senha field
      setConfirmaSenha(''); // Clear confirmaSenha field
      setSubmitting(false);
      return;
    }

    // Validar email com regex simples
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.emailPessoal)) {
      CustomSwal.show({
        title: 'Email Inválido',
        html: 'Por favor, insira um email válido.',
        type: 'error',
        confirmText: 'Fechar',
      });
      setSubmitting(false);
      return;
    }

    try {
      const response = await httpClient.post(`/api/users`, formData);

      // Extrai o primeiro nome do usuário e configura o estado
      const nomeUsuario = response.data.user.nomeCompleto.split(' ')[0];
      setNomeUsuarioCadastro(nomeUsuario);

      // Exibe a mensagem de sucesso personalizada com o nome do usuário
      CustomSwal.show({
        title: 'Cadastro realizado com sucesso!',
        html: 'No momento, você <b>ainda não possui acesso ao SisAF</b>, pois não está vinculado a uma <b>Unidade DAF, SES ou Fornecedor</b>.<br><br>Para liberar o acesso, solicite ao <b>administrador do seu setor</b> a realização da sua <b>vinculação</b> no sistema.',
        type: 'success',
        confirmText: 'Fechar',
        onConfirm: () => {
          navigate('/');
        },
        width: '45vw',
      });
    } catch (error) {
      // Lida com erros de resposta do Axios e exibe a mensagem apropriada
      const errorMessage =
        isHttpError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'Erro inesperado. Tente novamente.';

      // Exibe a mensagem de erro com swalErro
      CustomSwal.show({
        title: 'Cadastro não realizado',
        html: errorMessage,
        type: 'error',
        confirmText: 'Fechar',
      });

      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContainerBase>
      <GlobalStyle />
      <ContainerCadastro>
        <SecaoEsquerda>
          <img src={logoSisdaf} alt="Logo SisDAF" />
          <div>
            <span>Me cadastrar</span>
            <p style={{ textAlign: 'center' }}>
              Para solicitar acesso ao SIS<b>AF</b> é necessário efetuar o
              cadastro.
            </p>
          </div>
        </SecaoEsquerda>
        <SecaoDireita>
          <IconLogoContainer>
            <a href="/">
              <span className="material-icons">arrow_back</span>
            </a>
            <img src={logoGov} alt="Logo do SisDAF" />
          </IconLogoContainer>
          <FormContainer onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Input
                  name="cpf"
                  type="text"
                  mascara="cpf"
                  value={formData.cpf}
                  id="cpf"
                  label="CPF"
                  placeholder="Obrigatório"
                  onChange={handleChange}
                  onBlur={validaCPF}
                  required
                />
              </Col>
              <Col>
                <Input
                  name="nomeCompleto"
                  type="text"
                  value={formData.nomeCompleto}
                  id="nome"
                  label="Nome Completo"
                  placeholder="Obrigatório"
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Input
                  name="emailPessoal"
                  type="email"
                  value={formData.emailPessoal}
                  id="email"
                  label="Email"
                  placeholder="Obrigatório"
                  onChange={handleChange}
                  required
                />
              </Col>
              <Col>
                <Input
                  name="celular"
                  type="text"
                  mascara="celular"
                  value={formData.celular}
                  id="celular"
                  label="Celular"
                  placeholder="Obrigatório"
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Input
                  name="senha"
                  type="password"
                  value={formData.senha}
                  id="senha"
                  label="Senha"
                  placeholder="Obrigatório"
                  onChange={setSenha}
                  onBlur={validaSenhaTamanho}
                  required
                />
              </Col>
              <Col>
                <Input
                  name="confirmaSenha"
                  type="password"
                  value={confirmaSenha}
                  id="confirmaSenha"
                  label="Confirme sua Senha"
                  placeholder="Obrigatório"
                  onChange={handleConfirmaSenha}
                  onBlur={validaSenhaConfirma}
                  required
                />
              </Col>
            </Row>
            <ButtonContainer>
              <Button
                label="Confirmar"
                type="submit"
                disabled={submitting}
                botaoAzul
              />
            </ButtonContainer>
          </FormContainer>
        </SecaoDireita>
      </ContainerCadastro>
    </ContainerBase>
  );
};

export default Cadastrar;
